import {
  ChartBarIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationCircleIcon,
  PlayIcon,
} from "@heroicons/react/24/outline";
import { useMutation, useQuery } from "@tanstack/react-query";
import React, { useState } from "react";
import { apiService, handleApiError } from "../services/api";

export default function Analysis() {
  const [tickers, setTickers] = useState<string>("");
  const [analysisType, setAnalysisType] = useState<string>("standard");
  const [emailNotifications, setEmailNotifications] = useState<boolean>(true);
  const [slackNotifications, setSlackNotifications] = useState<boolean>(false);

  // Real API mutation for running analysis
  const analysisMutation = useMutation({
    mutationFn: apiService.runAnalysis,
    onSuccess: (data) => {
      console.log("Analysis completed:", data);
    },
    onError: (error) => {
      console.error("Analysis failed:", error);
    },
  });

  // Get recent analysis history
  const { data: analysisHistory, error: historyError } = useQuery({
    queryKey: ["analysis-history-preview"],
    queryFn: () => apiService.getAnalysisHistory(3),
    refetchInterval: 30000,
    retry: 1,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const tickerList = tickers
      .split(",")
      .map((ticker) => ticker.trim().toUpperCase())
      .filter((ticker) => ticker.length > 0);

    if (tickerList.length === 0) {
      return;
    }

    analysisMutation.mutate({
      tickers: tickerList,
      analysis_type: analysisType,
      email_notifications: emailNotifications,
      slack_notifications: slackNotifications,
    });
  };

  const getAnalysisTypeDescription = (type: string) => {
    switch (type) {
      case "quick":
        return "Fast analysis with 1 month of data - Perfect for quick insights";
      case "standard":
        return "Comprehensive analysis with 1 year of data - Recommended for most use cases";
      case "deep":
        return "In-depth analysis with 2+ years of data - Best for detailed research";
      default:
        return "";
    }
  };

  return (
    <div className="space-y-6">
      {/* Analysis Form */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <div className="card">
            <div className="flex items-center mb-6">
              <ChartBarIcon className="h-6 w-6 text-primary-600 mr-2" />
              <h2 className="text-xl font-semibold text-gray-900">
                Run Financial Analysis
              </h2>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="tickers" className="label">
                  Stock Tickers
                </label>
                <input
                  type="text"
                  id="tickers"
                  value={tickers}
                  onChange={(e) => setTickers(e.target.value)}
                  className="input"
                  placeholder="AAPL, MSFT, GOOGL, TSLA"
                  required
                />
                <p className="mt-2 text-sm text-gray-500">
                  Enter comma-separated stock ticker symbols (e.g., AAPL, MSFT,
                  GOOGL)
                </p>
              </div>

              <div>
                <label htmlFor="analysisType" className="label">
                  Analysis Type
                </label>
                <select
                  id="analysisType"
                  value={analysisType}
                  onChange={(e) => setAnalysisType(e.target.value)}
                  className="select"
                >
                  <option value="quick">Quick Analysis</option>
                  <option value="standard">Standard Analysis</option>
                  <option value="deep">Deep Analysis</option>
                </select>
                <p className="mt-2 text-sm text-gray-500">
                  {getAnalysisTypeDescription(analysisType)}
                </p>
              </div>

              <div className="space-y-3">
                <p className="label">Notifications</p>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={emailNotifications}
                      onChange={(e) => setEmailNotifications(e.target.checked)}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">
                      Email report when complete
                    </span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={slackNotifications}
                      onChange={(e) => setSlackNotifications(e.target.checked)}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">
                      Send Slack notification
                    </span>
                  </label>
                </div>
              </div>

              <button
                type="submit"
                disabled={analysisMutation.isPending || !tickers.trim()}
                className="btn-primary w-full"
              >
                {analysisMutation.isPending ? (
                  <>
                    <div className="spinner mr-2" />
                    Running Analysis...
                  </>
                ) : (
                  <>
                    <PlayIcon className="h-4 w-4 mr-2" />
                    Run Analysis
                  </>
                )}
              </button>
            </form>
          </div>
        </div>

        {/* Analysis Info */}
        <div className="space-y-6">
          <div className="card">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Analysis Features
            </h3>
            <div className="space-y-3">
              <div className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-success-600 mt-0.5 mr-3 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    Real-time Data
                  </p>
                  <p className="text-xs text-gray-500">
                    Live market data from Yahoo Finance
                  </p>
                </div>
              </div>
              <div className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-success-600 mt-0.5 mr-3 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    AI Sentiment Analysis
                  </p>
                  <p className="text-xs text-gray-500">
                    LLaMA-3 powered sentiment analysis
                  </p>
                </div>
              </div>
              <div className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-success-600 mt-0.5 mr-3 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    Risk Assessment
                  </p>
                  <p className="text-xs text-gray-500">
                    VaR, Sharpe ratio, portfolio optimization
                  </p>
                </div>
              </div>
              <div className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-success-600 mt-0.5 mr-3 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    Professional Reports
                  </p>
                  <p className="text-xs text-gray-500">
                    PDF and HTML report generation
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Quick Start
            </h3>
            <div className="space-y-3 text-sm text-gray-600">
              <div className="flex items-center">
                <span className="flex-shrink-0 w-6 h-6 bg-primary-100 text-primary-600 rounded-full flex items-center justify-center text-xs font-medium mr-3">
                  1
                </span>
                Enter stock tickers (e.g., AAPL, MSFT)
              </div>
              <div className="flex items-center">
                <span className="flex-shrink-0 w-6 h-6 bg-primary-100 text-primary-600 rounded-full flex items-center justify-center text-xs font-medium mr-3">
                  2
                </span>
                Choose analysis type
              </div>
              <div className="flex items-center">
                <span className="flex-shrink-0 w-6 h-6 bg-primary-100 text-primary-600 rounded-full flex items-center justify-center text-xs font-medium mr-3">
                  3
                </span>
                Configure notifications
              </div>
              <div className="flex items-center">
                <span className="flex-shrink-0 w-6 h-6 bg-primary-100 text-primary-600 rounded-full flex items-center justify-center text-xs font-medium mr-3">
                  4
                </span>
                Run analysis and get results
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Analysis Results */}
      {(analysisMutation.isPending ||
        analysisMutation.data ||
        analysisMutation.error) && (
        <div className="card">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Analysis Results
          </h3>

          {analysisMutation.isPending && (
            <div className="flex items-center justify-center py-8">
              <div className="text-center">
                <div className="spinner w-8 h-8 mx-auto mb-4" />
                <p className="text-gray-600">Running analysis...</p>
                <p className="text-sm text-gray-500 mt-1">
                  This may take a few moments depending on the analysis type
                </p>
              </div>
            </div>
          )}

          {analysisMutation.data && (
            <div className="bg-success-50 border border-success-200 rounded-lg p-4">
              <div className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-success-600 mt-0.5 mr-3 flex-shrink-0" />
                <div className="flex-1">
                  <h4 className="text-sm font-medium text-success-800">
                    Analysis Complete!
                  </h4>
                  <div className="mt-2 text-sm text-success-700">
                    <p>
                      <strong>Status:</strong> {analysisMutation.data.status}
                    </p>
                    <p>
                      <strong>Execution Time:</strong>{" "}
                      {analysisMutation.data.execution_time}s
                    </p>
                    <p>
                      <strong>Tickers:</strong>{" "}
                      {analysisMutation.data.tickers.join(", ")}
                    </p>
                    <p>
                      <strong>Task ID:</strong> {analysisMutation.data.task_id}
                    </p>
                  </div>
                  <div className="mt-3">
                    <button className="btn-primary text-sm">
                      View Detailed Report
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {analysisMutation.error && (
            <div className="bg-danger-50 border border-danger-200 rounded-lg p-4">
              <div className="flex items-start">
                <ExclamationCircleIcon className="h-5 w-5 text-danger-600 mt-0.5 mr-3 flex-shrink-0" />
                <div className="flex-1">
                  <h4 className="text-sm font-medium text-danger-800">
                    Analysis Failed
                  </h4>
                  <p className="mt-2 text-sm text-danger-700">
                    {analysisMutation.error.message ||
                      "An error occurred while running the analysis"}
                  </p>
                  <div className="mt-3">
                    <button
                      onClick={() => analysisMutation.reset()}
                      className="btn-secondary text-sm"
                    >
                      Try Again
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Analysis History Preview */}
      <div className="card">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Recent Analyses
        </h3>
        <div className="space-y-3">
          {historyError ? (
            <div className="text-center py-4 text-gray-500">
              <p>Unable to load recent analyses</p>
              <p className="text-xs mt-1">{handleApiError(historyError)}</p>
            </div>
          ) : !analysisHistory?.history?.length ? (
            <div className="text-center py-4 text-gray-500">
              No recent analyses found
            </div>
          ) : (
            analysisHistory.history.map((analysis) => (
              <div
                key={analysis.id}
                className="flex items-center justify-between py-3 border-b border-gray-200 last:border-b-0"
              >
                <div className="flex items-center space-x-3">
                  <div className="flex space-x-1">
                    {analysis.tickers.map((ticker) => (
                      <span
                        key={ticker}
                        className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-800"
                      >
                        {ticker}
                      </span>
                    ))}
                  </div>
                  <span className="text-sm text-gray-500">•</span>
                  <span className="text-sm text-gray-600 capitalize">
                    {analysis.analysis_type || "standard"} analysis
                  </span>
                </div>
                <div className="flex items-center space-x-3">
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      analysis.status === "completed"
                        ? "bg-success-100 text-success-800"
                        : analysis.status === "running"
                        ? "bg-blue-100 text-blue-800"
                        : "bg-gray-100 text-gray-800"
                    }`}
                  >
                    {analysis.status}
                  </span>
                  <span className="text-sm text-gray-500">
                    {new Date(analysis.timestamp).toLocaleString()}
                  </span>
                  <ClockIcon className="h-4 w-4 text-gray-400" />
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
